import React from "react";
import { motion } from "framer-motion";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import ContactForm from "./ContactForm";
import SocialLinks from "./SocialLinks";

const ContactSection = () => {
  const { elementRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.01,
    rootMargin: "0px 0px -50px 0px",
    triggerOnce: true,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        duration: 0.6,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const contactInfo = [
    {
      title: "Email",
      value: "emmanuelboye1957@gmail.com",
      href: "mailto:emmanuelboye1957@gmail.com",
      icon: "📧",
    },
    {
      title: "Phone & WhatsApp",
      value: "(+233) 59 35 01488",
      href: "https://wa.me/233593501488",
      icon: "💬",
    },
    {
      title: "Location",
      value: "Accra, Ghana",
      href: "https://share.google/eQzgVtkC0wHoZ6Wa9",
      icon: "📍",
    },
    {
      title: "Response Time",
      value: "Within 24 hours",
      href: null,
      icon: "⏱️",
    },
  ];

  return (
    <section
      id="contact"
      className="section-padding bg-gray-50 dark:bg-dark-900 relative overflow-hidden"
      ref={elementRef}
    >
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500 rounded-full filter blur-3xl animate-pulse" />
        <div
          className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-accent-500 rounded-full filter blur-3xl animate-pulse"
          style={{ animationDelay: "1s" }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isIntersecting ? "visible" : "hidden"}
          className="max-w-6xl mx-auto"
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <motion.span
              className="text-primary-500 font-medium text-lg mb-4 block"
              variants={itemVariants}
            >
              Get In Touch
            </motion.span>
            <motion.h2
              className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6"
              variants={itemVariants}
            >
              Let's Work <span className="text-primary-500">Together</span>
            </motion.h2>
            <motion.p
              className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto"
              variants={itemVariants}
            >
              Have a project in mind? I'd love to hear about it. Let's discuss
              how we can bring your ideas to life.
            </motion.p>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Column - Contact Info */}
            <motion.div variants={itemVariants} className="space-y-8">
              {/* Contact Information Cards */}
              <div className="space-y-6">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 dark:text-white mb-6">
                  Contact Information
                </h3>

                {contactInfo.map((info, index) => (
                  <motion.div
                    key={info.title}
                    variants={itemVariants}
                    className="flex items-center p-4 bg-white dark:bg-dark-900 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
                    whileHover={{ x: 5, scale: 1.02 }}
                  >
                    <div className="text-3xl mr-4 group-hover:scale-110 transition-transform">
                      {info.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {info.title}
                      </h4>
                      {info.href ? (
                        <a
                          href={info.href}
                          className="text-gray-600 dark:text-gray-400 hover:text-primary-500 transition-colors"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-gray-600 dark:text-gray-400">
                          {info.value}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Social Links */}
              <motion.div variants={itemVariants}>
                <h3 className="text-2xl font-heading font-semibold text-gray-900 dark:text-white mb-6">
                  Follow Me
                </h3>
                <SocialLinks />
              </motion.div>

              {/* Availability Status */}
              <motion.div
                variants={itemVariants}
                className="p-6 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800"
              >
                <div className="flex items-center mb-4">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse mr-3"></div>
                  <h4 className="font-semibold text-green-800 dark:text-green-200">
                    Available for New Projects
                  </h4>
                </div>
                <p className="text-green-700 dark:text-green-300 text-sm">
                  I'm currently accepting new freelance projects and full-time
                  opportunities. Let's create something amazing together!
                </p>
              </motion.div>
            </motion.div>

            {/* Right Column - Contact Form */}
            <motion.div variants={itemVariants}>
              <ContactForm />
            </motion.div>
          </div>

          {/* Bottom CTA */}
          <motion.div variants={itemVariants} className="text-center mt-16">
            <div className="max-w-2xl mx-auto p-8 bg-white dark:bg-dark-900 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-heading font-bold text-gray-900 dark:text-white mb-4">
                Ready to Start Your Project?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                From concept to deployment, I'll help you build something
                extraordinary. Let's discuss your vision and make it reality.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="mailto:emmanuelboye1957@gmail.com"
                  className="px-8 py-4 bg-primary-500 text-white font-semibold rounded-full hover:shadow-lg transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Send Email
                </motion.a>
                <motion.a
                  href="/docs/Boye_Emmanuel_Tete_CV.pdf"
                  download="Boye_Emmanuel_Tete_CV.pdf"
                  className="px-8 py-4 border-2 border-primary-500 text-primary-500 dark:text-primary-400 font-semibold rounded-full hover:bg-primary-500 hover:text-white transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Download CV
                </motion.a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
